import {
    Badge,
    Body1Strong,
    Button,
    Card,
    Combobox,
    Label,
    makeStyles,
    Option,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellActions,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Title3,
    tokens
} from "@fluentui/react-components";
import {AddCircle24Regular, ArrowLeft24Regular, Delete24Regular} from "@fluentui/react-icons";
import React, {useCallback, useEffect, useState} from "react";
import {errorAtom, graphClientAtom, groupsTabViewAtom, teamsUserCredentialAtom} from "../../../store.ts";
import {useAtomValue, useSetAtom} from "jotai";
import {useGraphClient} from "../../../hooks";
import {useTranslation} from "react-i18next";
import debounce from 'lodash.debounce';
import {ConfirmAudienceModal} from "../teams/ConfirmModal.tsx";

interface GraphUser {
    id: string;
    displayName: string;
    userPrincipalName: string;
    mail?: string;
    jobTitle?: string;
    department?: string;
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        '@media (max-width: 640px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    searchContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%',
        marginBottom: '24px',
    },
    searchInputWrapper: {
        position: 'relative',
        width: '100%',
    },
    searchIcon: {
        position: 'absolute',
        top: '8px',
        left: '8px',
        color: tokens.colorNeutralForeground3,
    },
    userItem: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: '4px',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        marginBottom: '8px',
        backgroundColor: tokens.colorNeutralBackground1,
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    userInfo: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    userName: {
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    userEmail: {
        color: tokens.colorNeutralForeground2,
        fontSize: '12px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    userDetail: {
        fontSize: '12px',
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '12px',
        marginTop: '16px',
    },
    selectedUsersSection: {
        marginTop: '24px',
    },
    selectedUsersList: {
        marginTop: '12px',
        maxHeight: '300px',
        overflowY: 'auto',
    },
    noResults: {
        textAlign: 'center',
        padding: '16px',
        color: tokens.colorNeutralForeground3,
    },
    comboBox: {
        width: '100%',
    },
    loadingIndicator: {
        display: 'flex',
        justifyContent: 'center',
        padding: '16px',
    },
    countBadge: {
        marginLeft: '8px',
    },
    responsiveTable: {
        '@media (max-width: 640px)': {
            '& table': {
                display: 'block',
                width: '100%',
            },
            '& thead': {
                display: 'none',
            },
            '& tbody': {
                display: 'block',
                width: '100%',
            },
            '& tr': {
                display: 'flex',
                flexDirection: 'column',
                padding: '12px 0',
                marginBottom: '8px',
                '&:not(:last-child)': {
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    paddingBottom: '16px',
                },
            },
            '& td': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: 'none',
                padding: '4px 0',
                width: '100%',
                position: 'relative',
                '&::before': {
                    content: 'attr(data-label)',
                    fontWeight: 'bold',
                    marginRight: '8px',
                },
                '&:last-child': {
                    justifyContent: 'flex-end',
                    marginTop: '8px',
                },
            },
            '& [role="cell-actions"]': {
                opacity: '1 !important',
                //position: 'static !important',
                transform: 'none !important',
                padding: '0 !important',
            },
        }
    }
});

export const AddAudienceIndividualsView: React.FC = () => {
    const styles = useStyles();
    const {t} = useTranslation();
    const setError = useSetAtom(errorAtom);
    const teamsUserCredential = useAtomValue(teamsUserCredentialAtom);
    const setGraphClient = useSetAtom(graphClientAtom);
    const graphClient = useAtomValue(graphClientAtom);
    const setGroupView = useSetAtom(groupsTabViewAtom);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [searchResults, setSearchResults] = useState<GraphUser[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<GraphUser[]>([]);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

    const {
        getGraphClient,
        error: graphError,
        needsConsent,
        requestConsent
    } = useGraphClient(
        teamsUserCredential,
        ["User.ReadBasic.All", "Team.ReadBasic.All", "TeamMember.Read.All"]
    );

    useEffect(() => {
        getGraphClient().then(client => {
            setGraphClient(client);
            setError(null)
        })
    }, []);

    useEffect(() => {
        setError(graphError)
    }, [graphError]);

    useEffect(() => {
        if (needsConsent) {
            requestConsent().then(success => {
                if (success) {
                    getGraphClient().then(client => {
                        setGraphClient(client);
                        setError(null)
                    })
                }
            })
        }
    }, [needsConsent]);


    // Debounced search function
    const debouncedSearch = useCallback(
        debounce(async (term: string) => {
            if (!graphClient || term.length < 2) {
                setSearchResults([]);
                setIsSearching(false);
                return;
            }

            try {
                setIsSearching(true);
                // Search for users with the given term in their display name or email
                const response = await graphClient
                    .api('/users')
                    .search(`"displayName:${term}" OR "mail:${term}" OR "userPrincipalName:${term}"`)
                    .header("ConsistencyLevel", "eventual")
                    .select(['id', 'displayName', 'mail'])
                    .top(10)
                    .get();

                if (response && response.value) {
                    setSearchResults(response.value);
                } else {
                    setSearchResults([]);
                }
            } catch (error: any) {
                console.error('Error searching users:', error);
                setError(`Failed to search users: ${error.message}`);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 500),
        [graphClient]
    );

    // Handle search input change
    useEffect(() => {
        if (searchTerm.length >= 2) {
            debouncedSearch(searchTerm);
        } else {
            setSearchResults([]);
        }
        return () => debouncedSearch.cancel();
    }, [searchTerm, debouncedSearch]);

    const handleAddUser = (user: GraphUser) => {
        if (!selectedUsers.some(selectedUser => selectedUser.id === user.id)) {
            setSelectedUsers([...selectedUsers, user]);
        }
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleRemoveUser = (userId: string) => {
        setSelectedUsers(selectedUsers.filter(user => user.id !== userId));
    };

    const handleBack = () => {
        setGroupView('list');
    };

    const handleCreateAudience = () => {
        if (selectedUsers.length > 0) {
            setShowConfirmModal(true);
        } else {
            setError(t("error.no-audience-participants"));
        }
    };

    // Convert GraphUser to Participant format needed by ConfirmAudienceModal
    const selectedParticipants = selectedUsers.map(user => ({
        id: user.id,
    }));

    // Handle post-success actions
    const handlePostSuccess = () => {
        setSelectedUsers([]);
        setSearchTerm('');
        setSearchResults([]);
        setShowConfirmModal(false);
        setGroupView('list');
    };

    return (
        <Card>
            <div className={styles.container}>
                <div className={styles.header}>
                    <Title3>{t("text.select-participants")}</Title3>
                </div>

                <div className={styles.searchContainer}>
                    <Label htmlFor="userSearch">{t("text.search-users")}</Label>
                    <div className={styles.searchInputWrapper}>
                        <Combobox
                            className={styles.comboBox}
                            placeholder={t("placeholder.search-by-name-email")}
                            value={searchTerm}
                            onInput={(e) => setSearchTerm(e.currentTarget.value)}
                            onOptionSelect={(event, option) => {
                                if (option.optionValue && option.optionValue !== "") {
                                    const selectedUser = searchResults.find(
                                        user => user.id === option.optionValue
                                    );
                                    if (selectedUser) {
                                        handleAddUser(selectedUser);
                                    }
                                }
                            }}
                            freeform
                        >
                            {isSearching ? (
                                <Option text={t("text.loading")} disabled>
                                    <Spinner size="tiny"/>{t("text.loading")}
                                </Option>
                            ) : searchResults.length > 0 ? (
                                searchResults.map(user => (
                                    <Option key={user.id} value={user.id} text={user.displayName}>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                fontWeight: 'bold',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {user.displayName}
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: tokens.colorNeutralForeground2,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {user.mail || user.userPrincipalName}
                                            </div>
                                            {(user.jobTitle || user.department) && (
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: tokens.colorNeutralForeground3,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {[user.jobTitle, user.department]
                                                        .filter(Boolean)
                                                        .join(' • ')}
                                                </div>
                                            )}
                                        </div>
                                    </Option>
                                ))
                            ) : searchTerm.length >= 2 ? (
                                <Option disabled>
                                    {t("text.no-users-found")}
                                </Option>
                            ) : (
                                <Option disabled>
                                    {t("text.type-to-search")}
                                </Option>
                            )}
                        </Combobox>
                    </div>
                </div>

                <div className={styles.selectedUsersSection}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px'
                    }}>
                        <Body1Strong>
                            {t("text.selected-users")}
                            <Badge
                                appearance="filled"
                                color={selectedUsers.length > 0 ? "success" : "warning"}
                                className={styles.countBadge}
                            >
                                {selectedUsers.length}
                            </Badge>
                        </Body1Strong>

                        {selectedUsers.length > 0 && (
                            <Button
                                appearance="subtle"
                                size="small"
                                onClick={() => setSelectedUsers([])}
                            >
                                {t("button.clear")}
                            </Button>
                        )}
                    </div>

                    {selectedUsers.length === 0 ? (
                        <div className={styles.noResults}>
                            {t("text.no-users-selected")}
                        </div>
                    ) : (
                        <div style={{overflowX: 'auto'}}>
                            <Table aria-label="Selected users table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell>{t("table.header.name")}</TableHeaderCell>
                                        <TableHeaderCell>{t("table.header.email")}</TableHeaderCell>
                                        <TableHeaderCell>{t("table.header.action")}</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {selectedUsers.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell data-label={t("table.header.name")}>
                                                <div>
                                                    <div>{user.displayName}</div>
                                                    {(user.jobTitle || user.department) && (
                                                        <span style={{
                                                            fontSize: '12px',
                                                            color: tokens.colorNeutralForeground3,
                                                        }}>
                                                            {[user.jobTitle, user.department]
                                                                .filter(Boolean)
                                                                .join(' • ')}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell data-label={t("table.header.email")}
                                                       style={{overflow: "hidden", textOverflow: "ellipsis"}}>
                                                {user.mail || user.userPrincipalName}
                                            </TableCell>
                                            <TableCell data-label={t("table.header.action")}>
                                                <TableCellActions>
                                                    <Button
                                                        icon={<Delete24Regular/>}
                                                        onClick={() => handleRemoveUser(user.id)}
                                                        aria-label={t("button.remove")}
                                                    >
                                                        {t("button.delete")}
                                                    </Button>
                                                </TableCellActions>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>

                <div className={styles.buttonContainer}>
                    <Button
                        appearance="secondary"
                        icon={<ArrowLeft24Regular/>}
                        onClick={handleBack}
                    >
                        {t("button.back-to-audiences-list")}
                    </Button>
                    <Button
                        appearance="primary"
                        icon={<AddCircle24Regular/>}
                        disabled={selectedUsers.length === 0}
                        onClick={handleCreateAudience}
                    >
                        {t("button.create-audience")}
                    </Button>
                </div>
            </div>

            {showConfirmModal && (
                <ConfirmAudienceModal
                    selectedParticipants={selectedParticipants}
                    closeModal={() => setShowConfirmModal(false)}
                    postSuccess={handlePostSuccess}
                />
            )}
        </Card>
    );
}
